// src/services/offline/indexedDB.service.ts
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Workload } from '../../types/workload.types';
import { WorkloadAddress } from '../../types/address.types';
import { Survey, SurveyResponse } from '../../types/survey.types';
import { SyncQueueItem, SyncStatus } from '../../types/offline.types';

interface CanvassedDB extends DBSchema {
  workloads: {
    key: string;
    value: Workload & { syncStatus: SyncStatus; localVersion: number };
    indexes: {
      'by-projectId': string;
      'by-status': string;
      'by-syncStatus': SyncStatus;
    };
  };
  addresses: {
    key: string;
    value: WorkloadAddress & { syncStatus: SyncStatus; localVersion: number };
    indexes: {
      'by-workloadId': string;
      'by-status': string;
      'by-syncStatus': SyncStatus;
    };
  };
  surveys: {
    key: string;
    value: Survey & { syncStatus: SyncStatus };
    indexes: {
      'by-projectId': string;
      'by-status': string;
    };
  };
  surveyResponses: {
    key: string;
    value: SurveyResponse & { syncStatus: SyncStatus };
    indexes: {
      'by-workloadId': string;
      'by-surveyId': string;
      'by-syncStatus': SyncStatus;
    };
  };
  syncQueue: {
    key: string;
    value: SyncQueueItem;
    indexes: {
      'by-status': SyncStatus;
      'by-collection': string;
      'by-timestamp': number;
    };
  };
  meta: {
    key: string;
    value: { key: string; value: any };
  };
}

const DB_NAME = 'canvassed-offline';
const DB_VERSION = 1;

class IndexedDBService {
  private db: IDBPDatabase<CanvassedDB> | null = null;
  private initPromise: Promise<IDBPDatabase<CanvassedDB>> | null = null;

  async init(): Promise<IDBPDatabase<CanvassedDB>> {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = openDB<CanvassedDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Workloads store
        if (!db.objectStoreNames.contains('workloads')) {
          const workloadsStore = db.createObjectStore('workloads', { keyPath: 'id' });
          workloadsStore.createIndex('by-projectId', 'projectId');
          workloadsStore.createIndex('by-status', 'status');
          workloadsStore.createIndex('by-syncStatus', 'syncStatus');
        }

        // Addresses store
        if (!db.objectStoreNames.contains('addresses')) {
          const addressesStore = db.createObjectStore('addresses', { keyPath: 'id' });
          addressesStore.createIndex('by-workloadId', 'workloadId');
          addressesStore.createIndex('by-status', 'status');
          addressesStore.createIndex('by-syncStatus', 'syncStatus');
        }

        // Surveys store
        if (!db.objectStoreNames.contains('surveys')) {
          const surveysStore = db.createObjectStore('surveys', { keyPath: 'id' });
          surveysStore.createIndex('by-projectId', 'projectId');
          surveysStore.createIndex('by-status', 'status');
        }

        // Survey responses store
        if (!db.objectStoreNames.contains('surveyResponses')) {
          const responsesStore = db.createObjectStore('surveyResponses', { keyPath: 'id' });
          responsesStore.createIndex('by-workloadId', 'workloadId');
          responsesStore.createIndex('by-surveyId', 'surveyId');
          responsesStore.createIndex('by-syncStatus', 'syncStatus');
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const queueStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          queueStore.createIndex('by-status', 'status');
          queueStore.createIndex('by-collection', 'collection');
          queueStore.createIndex('by-timestamp', 'timestamp');
        }

        // Meta store
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });

    this.db = await this.initPromise;
    return this.db;
  }


  async getWorkload(id: string): Promise<(Workload & { syncStatus: SyncStatus }) | undefined> {
    const db = await this.init();
    return db.get('workloads', id);
  }

  async getAllWorkloads(projectId?: string): Promise<(Workload & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    if (projectId) {
      return db.getAllFromIndex('workloads', 'by-projectId', projectId);
    }
    return db.getAll('workloads');
  }

  async saveWorkload(workload: Workload, syncStatus: SyncStatus = 'pending'): Promise<void> {
    const db = await this.init();
    const existing = await db.get('workloads', workload.id);
    const localVersion = (existing?.localVersion || 0) + 1;
    await db.put('workloads', { ...workload, syncStatus, localVersion });
  }

  async saveWorkloads(workloads: Workload[], syncStatus: SyncStatus = 'synced'): Promise<void> {
    const db = await this.init();
    const tx = db.transaction('workloads', 'readwrite');
    await Promise.all([
      ...workloads.map((w) => tx.store.put({ ...w, syncStatus, localVersion: 1 })),
      tx.done,
    ]);
  }

  async deleteWorkload(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('workloads', id);
  }

  async getUnsyncdWorkloads(): Promise<(Workload & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    return db.getAllFromIndex('workloads', 'by-syncStatus', 'pending');
  }


  async getAddress(id: string): Promise<(WorkloadAddress & { syncStatus: SyncStatus }) | undefined> {
    const db = await this.init();
    return db.get('addresses', id);
  }

  async getWorkloadAddresses(workloadId: string): Promise<(WorkloadAddress & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    return db.getAllFromIndex('addresses', 'by-workloadId', workloadId);
  }

  async saveAddress(address: WorkloadAddress, syncStatus: SyncStatus = 'pending'): Promise<void> {
    const db = await this.init();
    const existing = await db.get('addresses', address.id);
    const localVersion = (existing?.localVersion || 0) + 1;
    await db.put('addresses', { ...address, syncStatus, localVersion });
  }

  async saveAddresses(addresses: WorkloadAddress[], syncStatus: SyncStatus = 'synced'): Promise<void> {
    const db = await this.init();
    const tx = db.transaction('addresses', 'readwrite');
    await Promise.all([
      ...addresses.map((a) => tx.store.put({ ...a, syncStatus, localVersion: 1 })),
      tx.done,
    ]);
  }

  async deleteAddress(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('addresses', id);
  }

  async getUnsyncdAddresses(): Promise<(WorkloadAddress & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    return db.getAllFromIndex('addresses', 'by-syncStatus', 'pending');
  }


  async getSurvey(id: string): Promise<(Survey & { syncStatus: SyncStatus }) | undefined> {
    const db = await this.init();
    return db.get('surveys', id);
  }

  async getAllSurveys(projectId?: string): Promise<(Survey & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    if (projectId) {
      return db.getAllFromIndex('surveys', 'by-projectId', projectId);
    }
    return db.getAll('surveys');
  }

  async saveSurvey(survey: Survey, syncStatus: SyncStatus = 'synced'): Promise<void> {
    const db = await this.init();
    await db.put('surveys', { ...survey, syncStatus });
  }

  async saveSurveys(surveys: Survey[]): Promise<void> {
    const db = await this.init();
    const tx = db.transaction('surveys', 'readwrite');
    await Promise.all([
      ...surveys.map((s) => tx.store.put({ ...s, syncStatus: 'synced' })),
      tx.done,
    ]);
  }

  async deleteSurvey(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('surveys', id);
  }


  async getSurveyResponse(id: string): Promise<(SurveyResponse & { syncStatus: SyncStatus }) | undefined> {
    const db = await this.init();
    return db.get('surveyResponses', id);
  }

  async getSurveyResponses(surveyId?: string): Promise<(SurveyResponse & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    if (surveyId) {
      return db.getAllFromIndex('surveyResponses', 'by-surveyId', surveyId);
    }
    return db.getAll('surveyResponses');
  }

  async saveSurveyResponse(response: SurveyResponse, syncStatus: SyncStatus = 'pending'): Promise<void> {
    const db = await this.init();
    await db.put('surveyResponses', { ...response, syncStatus });
  }

  async deleteSurveyResponse(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('surveyResponses', id);
  }

  async getUnsyncdSurveyResponses(): Promise<(SurveyResponse & { syncStatus: SyncStatus })[]> {
    const db = await this.init();
    return db.getAllFromIndex('surveyResponses', 'by-syncStatus', 'pending');
  }


  async addToSyncQueue(item: SyncQueueItem): Promise<void> {
    const db = await this.init();
    await db.put('syncQueue', item);
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = await this.init();
    return db.getAllFromIndex('syncQueue', 'by-timestamp');
  }

  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    const db = await this.init();
    return db.getAllFromIndex('syncQueue', 'by-status', 'pending');
  }

  async updateSyncQueueItem(id: string, updates: Partial<SyncQueueItem>): Promise<void> {
    const db = await this.init();
    const item = await db.get('syncQueue', id);
    if (item) {
      await db.put('syncQueue', { ...item, ...updates });
    }
  }

  async removeSyncQueueItem(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('syncQueue', id);
  }

  async clearSyncQueue(): Promise<void> {
    const db = await this.init();
    await db.clear('syncQueue');
  }


  async getMeta<T>(key: string): Promise<T | undefined> {
    const db = await this.init();
    const item = await db.get('meta', key);
    return item?.value as T | undefined;
  }

  async setMeta<T>(key: string, value: T): Promise<void> {
    const db = await this.init();
    await db.put('meta', { key, value });
  }

  async deleteMeta(key: string): Promise<void> {
    const db = await this.init();
    await db.delete('meta', key);
  }


  async clearAll(): Promise<void> {
    const db = await this.init();
    await Promise.all([
      db.clear('workloads'),
      db.clear('addresses'),
      db.clear('surveys'),
      db.clear('surveyResponses'),
      db.clear('syncQueue'),
      db.clear('meta'),
    ]);
  }

  async getStats(): Promise<{
    workloads: number;
    addresses: number;
    surveys: number;
    responses: number;
    pendingSync: number;
  }> {
    const db = await this.init();
    const [workloads, addresses, surveys, responses, pendingSync] = await Promise.all([
      db.count('workloads'),
      db.count('addresses'),
      db.count('surveys'),
      db.count('surveyResponses'),
      db.countFromIndex('syncQueue', 'by-status', 'pending'),
    ]);
    return { workloads, addresses, surveys, responses, pendingSync };
  }
}

export const indexedDBService = new IndexedDBService();
